
import rest from "@/rest";
import _, { templateSettings } from "lodash";
import { Component, Vue, Watch, Prop } from "vue-property-decorator";
import { Action, namespace } from "vuex-class";
import ManageSubtitles from "../ContentCreator/ManageSubtitles.vue";
// interfaces
import { ElementSaveStateVm, ExChoiceList } from "@/interfaces/ExDataSet";
import { SubtitelVm } from "@/interfaces/SubtitelVm";

interface SubtitleCue {
  startTime: number;
  endTime: number;
  text: string;
}

const globals = namespace("globals");
const quizplayerstore = namespace("quizplayerstore");

// https://www.w3schools.com/tags/ref_av_dom.asp
@Component({
    components: {
      ManageSubtitles
    }
})
export default class CompVideo extends Vue {
  @Prop() guid!: string;
  @Prop() choiceList!: ExChoiceList;
  @Prop() finalized!: boolean;
  @Prop() src!: string;
  @Prop() width!: string;
  @Prop() height!: string;
  @Prop() compSettings!: string;

  @Prop() private ccPreventSeeking!: boolean; // Used by settings component
  @Prop() private ccEnableZoom!: boolean; // Used by settings component of Content Creator
  @Prop() private ccCreatedWithAi!: boolean; // Used by settings component of Content Creator
  @Prop() private ccEnableDownload!: boolean; // Used by settings component of Content Creator
  @Prop() private showManageSubtitles!: boolean;

  @quizplayerstore.Action setElementState!: (saveState: ElementSaveStateVm) => void;
  @quizplayerstore.Action getElementState!: (settings: string) => Promise<string | null>;

  settings: any = null;
  currentTime: any = null;
  showDialog = false;
  showCloseBtn = false;
  private subtitleText: string = "";
  private cues: SubtitleCue[] = [];
  private activeCueIndex: number | null = null;
  private subtitleObjectUrl: string | null = null;
  private subtitelFiles: SubtitelVm[] = [];
  private selectedSubtitle: SubtitelVm | null = null;
  private showManageSubtitlesDlg: boolean = false;
  lastAllowedTime: number = 0;
  private ytPlayer: any = null;
  private ytPollInterval: any = null;
  private ytSeekedBack: boolean = false; // true when we forced a seekTo – ignore resulting ENDED event

  $refs!: {
    l3VideoInline: any,
    l3VideoFullscreen: any
   };

  async mounted() {
    if (this.compSettings) {
      this.settings = JSON.parse(this.compSettings);
    }

    // Try to load existing state for this element
    if (this.choiceList?.items) {
      let saveState = await this.getElementState(this.guid);

      if (saveState && saveState == "1") {
        // Disable preventSeeking if already played once
        this.settings.preventSeeking = false;

        // Enable "Weiter" button if preventSeeking is active
        this.$store.commit("quizplayerstore/setChoiceState",
          { id: this.choiceList.id, questionId: this.choiceList.questionId, isRight: true, selectionLable: "" }
        );
      }
    }

    await this.loadSubtitles();

    // Setze initialen Subtitle, falls vorhanden
    if (this.subtitelFiles.length > 0) {
      this.selectedSubtitle = this.subtitelFiles[0];
    } else {
      this.selectedSubtitle = null;
    }

    // Initialize YouTube IFrame API if needed
    if (this.isYoutube) {
      await this.$nextTick();
      await this.loadYoutubeApi();
      this.initYtPlayer();
    }
  }

  beforeDestroy() {
    this.stopYtSeekPoll();
    if (this.ytPlayer) {
      try { this.ytPlayer.destroy(); } catch (_) {}
      this.ytPlayer = null;
    }
  }

  onSeeking(e: Event) {
    if (!this.preventSeeking) return;
    const video = e.target as HTMLVideoElement;
    if (video.currentTime > this.lastAllowedTime + 0.5) {
      video.currentTime = this.lastAllowedTime;
    }
  }

  onTimeUpdate(e: Event) {
    if (!this.preventSeeking) return;
    const video = e.target as HTMLVideoElement;
    if (video.currentTime > this.lastAllowedTime) {
      this.lastAllowedTime = video.currentTime;
    }
  }

  @Watch('preventSeeking')
  onPreventSeekingChanged(val: boolean) {
    if (!val) {
      // preventSeeking turned off: stop YT poll, clear tracked position
      this.stopYtSeekPoll();
      this.lastAllowedTime = 0;
    } else {
      // preventSeeking turned on: anchor lastAllowedTime to the current playback
      // position so the user can still seek within the already-played region
      if (this.isYoutube) {
        if (this.ytPlayer && typeof this.ytPlayer.getCurrentTime === 'function') {
          this.lastAllowedTime = this.ytPlayer.getCurrentTime();
          // Restart seek-poll if the player is currently playing
          const YTState = (window as any).YT?.PlayerState;
          if (YTState && typeof this.ytPlayer.getPlayerState === 'function' &&
              this.ytPlayer.getPlayerState() === YTState.PLAYING) {
            this.startYtSeekPoll();
          }
        }
      } else {
        const video = this.$refs.l3VideoInline ?? this.$refs.l3VideoFullscreen;
        if (video) {
          this.lastAllowedTime = video.currentTime ?? 0;
        }
      }
    }
  }

  // ── YouTube IFrame API helpers ─────────────────────────────────────────

  get youtubeIframeId(): string {
    return `yt-iframe-${this.guid || 'default'}`;
  }

  private loadYoutubeApi(): Promise<void> {
    return new Promise((resolve) => {
      const win = window as any;
      if (win.YT && win.YT.Player) {
        resolve();
        return;
      }
      const prev = win.onYouTubeIframeAPIReady;
      win.onYouTubeIframeAPIReady = () => {
        if (prev) prev();
        resolve();
      };
      if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        document.head.appendChild(tag);
      }
    });
  }

  private initYtPlayer() {
    const win = window as any;
    if (!win.YT || !win.YT.Player) return;
    this.ytPlayer = new win.YT.Player(this.youtubeIframeId, {
      events: {
        onStateChange: (event: any) => this.onYtStateChange(event),
      }
    });
  }

  private onYtStateChange(event: any) {
    const YTState = (window as any).YT?.PlayerState;
    if (!YTState) return;
    if (event.data === YTState.ENDED) {
      if (this.ytSeekedBack) {
        // ENDED was triggered by our own seekTo (seek-prevention bounce-back) – ignore it
        this.ytSeekedBack = false;
        this.ytPlayer?.seekTo(this.lastAllowedTime, true);
        return;
      }
      // Guard against seeking to end while paused (poll wasn't running in that case)
      if (this.preventSeeking) {
        const duration: number = typeof this.ytPlayer?.getDuration === 'function'
          ? this.ytPlayer.getDuration() : 0;
        if (duration > 0 && this.lastAllowedTime < duration - 2) {
          this.ytPlayer?.seekTo(this.lastAllowedTime, true);
          return;
        }
      }
      this.onVideoEnded();
    }
    if (event.data !== YTState.ENDED) {
      // Any non-ENDED state clears the flag (user started playing again after seek-back)
      this.ytSeekedBack = false;
    }
    if (this.preventSeeking) {
      if (event.data === YTState.PLAYING) {
        this.startYtSeekPoll();
      } else {
        this.stopYtSeekPoll();
      }
    }
  }

  private startYtSeekPoll() {
    this.stopYtSeekPoll();
    this.ytPollInterval = setInterval(() => {
      if (!this.ytPlayer || typeof this.ytPlayer.getCurrentTime !== 'function') return;
      const currentTime: number = this.ytPlayer.getCurrentTime();
      // Threshold of 1.5s: large enough to not trigger on normal playback
      // (poll runs every 250ms, so normal playback advances ~0.25s per tick),
      // but small enough to catch real forward seeks.
      if (currentTime > this.lastAllowedTime + 1.5) {
        this.ytSeekedBack = true;
        this.ytPlayer.seekTo(this.lastAllowedTime, true);
      } else if (currentTime > this.lastAllowedTime) {
        this.lastAllowedTime = currentTime;
      }
    }, 250);
  }

  private stopYtSeekPoll() {
    if (this.ytPollInterval !== null) {
      clearInterval(this.ytPollInterval);
      this.ytPollInterval = null;
    }
  }

  // ─────────────────────────────────────────────────────────────────────────

  private onVideoEnded() {
    if (this.finalized || !this.choiceList || !this.preventSeeking) return;

    // For native video: some browsers fire 'ended' even when onSeeking already bounced
    // currentTime back. Check actual currentTime – if it's not near the end, ignore it.
    if (!this.isYoutube) {
      const video = (this.$refs.l3VideoInline ?? this.$refs.l3VideoFullscreen) as HTMLVideoElement | null;
      if (video && isFinite(video.duration) && video.currentTime < video.duration - 0.5) {
        return;
      }
    }

    this.setElementState( {
        guid: this.guid,
        settings: "1"
      } as ElementSaveStateVm
    );

    // Enable "Weiter" button if preventSeeking is active
    this.$store.commit("quizplayerstore/setChoiceState",
      { id: this.choiceList.id, questionId: this.choiceList.questionId, isRight: true, selectionLable: "" }
    );
  }

  async loadSubtitles() {
    if (!this.src) return;
    console.log("Loading subtitles...");
    this.subtitelFiles = await rest.url("exercises/getSubtitles").query({ audioFile: this.src }).get();
    // Add element no subtitle
    this.subtitelFiles.unshift({
      label: "Keine Untertitel",
      source: "",
      languageCode: ""
    });
  }

  // Settings with priority over props
  get title(): string {
    return this.settings?.title || "";
  }

  get controlsList() {
    if (this.enableDownload)
      return "";
    return "nodownload"
  }

  get enableZoom() {
    // Only for desktop
    if (this.$vuetify.breakpoint.xs)
      return false;

    if (this.ccEnableZoom) return this.ccEnableZoom;

    return this.settings?.enableZoom ?? false;
  }

  get enableDownload() {
    if (this.ccEnableDownload) return this.ccEnableDownload;

    return this.settings?.enableDownload ?? false;
  }

  get preventSeeking() {
    if (this.ccPreventSeeking) return this.ccPreventSeeking;

    return this.settings?.preventSeeking ?? false;
  }

  get playOnLoad() {
    if (this.settings?.playOnLoad)
      return this.settings.playOnLoad;

    // return this.playOnLoad;
    return false; // Play on load only if shown in quiz player
  }

  get aiCreated() {
    if (this.ccCreatedWithAi !== undefined && this.ccCreatedWithAi !== null)
      return this.ccCreatedWithAi;
    return this.settings?.createdWithAi ?? false;
  }

  get isYoutube() {
    if (this.src && this.src.toLowerCase().includes("www.youtube.com/watch"))
      return true;
    return false;
  }

  get youtubeLink() {
    if (!this.isYoutube)
      return "";

    let startIdx = this.src.toLowerCase().indexOf("?v=");
    if (startIdx == -1)
      return "";
    const lowerSrc = this.src.toLowerCase();
    let endIdx = lowerSrc.indexOf(";", startIdx);
    const ampIdx = lowerSrc.indexOf("&", startIdx);
    const shouldUseAmp = ampIdx !== -1 && (endIdx === -1 || ampIdx < endIdx);
    if (shouldUseAmp) {
      endIdx = ampIdx;
    }
    if (endIdx == -1)
      endIdx = this.src.length;

    let ytCode = this.src.substring(startIdx + 3, endIdx);

    return `https://www.youtube-nocookie.com/embed/${ytCode}?enablejsapi=1${this.playOnLoad ? '&autoplay=1' : ''}`; //_2m_kdXVmJQ
  }

  playPause() {
    if (this.$refs.l3VideoInline.paused)
      this.$refs.l3VideoInline.play();
    else
      this.$refs.l3VideoInline.pause();
  }

  async onOpenDlg() {
    if (!this.enableZoom)
      return;

    let paused = this.$refs.l3VideoInline.paused;
    this.$refs.l3VideoInline.pause();
    this.currentTime = this.$refs.l3VideoInline.currentTime;

    this.showDialog = true;
    await this.$globalHelper.delay(250);
    this.$refs.l3VideoFullscreen.currentTime = this.currentTime;
    if (paused)
      this.$refs.l3VideoFullscreen.pause();
    else
      this.$refs.l3VideoFullscreen.play();
    this.showCloseBtn = true;
  }

  async onCloseDlg() {
    let paused = this.$refs.l3VideoFullscreen.paused;
    this.$refs.l3VideoFullscreen.pause();
    this.currentTime = this.$refs.l3VideoFullscreen.currentTime;

    this.showCloseBtn = false;
    this.showDialog = false;
    await this.$globalHelper.delay(100);
    this.$refs.l3VideoInline.currentTime = this.currentTime;
    if (paused)
      this.$refs.l3VideoInline.pause();
    else
      this.$refs.l3VideoInline.play();
  }

  get widthValue() {
    if (!this.width && this.height)
      return Number(this.height) / 0.5625 + "px";

    if (!this.width)
      return "100%";

    if (this.width.endsWith("px") || this.width.endsWith("%"))
        return this.width;

    return this.width + "px";
  }

  get heightValue() {
    if (!this.height)
      return "100%";

    if (this.height.endsWith("px") || this.height.endsWith("%"))
      return this.height;

    return this.height + "px";
  }

  get ytPadding() {
    if (this.width && !this.height)
      return Number(this.width) * 0.5625 + "px";

    if (!this.width && this.height || this.width && this.height)
    return "0px";

    return "56.25%";
  }
}
